//#region src/constants.ts
const MAX_MACRO_EXPANSION_DEPTH = 128;
const NodeTypes = {
	Num: "Num",
	Str: "Str",
	Atom: "Atom",
	Call: "Call",
	SpecialExpression: "SpecialExpression",
	Sym: "Sym",
	Builtin: "Builtin",
	Special: "Special",
	Reserved: "Reserved",
	Binding: "Binding",
	Spread: "Spread",
	TmplStr: "TmplStr",
	If: "If",
	Block: "Block",
	Effect: "Effect",
	Recur: "Recur",
	Array: "Array",
	Parallel: "Parallel",
	Race: "Race",
	Settled: "Settled",
	Perform: "Perform",
	Object: "Object",
	Function: "Function",
	Let: "Let",
	And: "And",
	Or: "Or",
	Qq: "Qq",
	Loop: "Loop",
	For: "For",
	Match: "Match",
	Import: "Import",
	Macro: "Macro",
	Handler: "Handler",
	Resume: "Resume",
	WithHandler: "WithHandler",
	CodeTmpl: "CodeTmpl",
	Splice: "Splice",
	InlinedData: "InlinedData",
	MacroCall: "MacroCall"
};
const NodeTypesSet = new Set(Object.values(NodeTypes));
function isNodeType(type) {
	return typeof type === "string" && NodeTypesSet.has(type);
}
const functionTypeSet = new Set([
	"UserDefined",
	"Macro",
	"Partial",
	"Comp",
	"Constantly",
	"Juxt",
	"Complement",
	"EveryPred",
	"SomePred",
	"Fnull",
	"QualifiedMatcher",
	"Builtin",
	"SpecialBuiltin",
	"Module",
	"Handler",
	"Resume"
]);
function isFunctionType(type) {
	return typeof type === "string" && functionTypeSet.has(type);
}
//#endregion
//#region src/symbols.ts
const FUNCTION_SYMBOL = "^^fn^^";
const REGEXP_SYMBOL = "^^re^^";
const EFFECT_SYMBOL = "^^ef^^";
const ATOM_SYMBOL = "^^at^^";
//#endregion
//#region src/persistent/PersistentVector.ts
/**
* PersistentVector — an immutable, structurally-shared vector based on a
* 32-way trie (branching factor 32).
*
* Performance characteristics:
*   get(i)     O(log₃₂ N) ≈ O(1) in practice (max depth ~6 for 1B elements)
*   set(i, v)  O(log₃₂ N) — path-copies the spine, shares all unchanged nodes
*   append(v)  O(log₃₂ N) amortized — O(1) when tail has room (tail holds ≤ 32 elems)
*   size       O(1)
*   iterate    O(N)
*
* Tail optimisation: the last ≤ 32 elements are kept in a flat array, so
* sequential appends and gets near the end never touch the trie.
*
* Small-collection threshold (future): for size ≤ 32, a flat array bypasses
* the trie entirely, eliminating per-node overhead for tiny vectors. Left as
* a known future optimization per design intent.
*/
const BITS$1 = 5;
const WIDTH = 1 << BITS$1;
const MASK$1 = WIDTH - 1;
function makeNode(arr) {
	return { array: arr };
}
function copyArray(node) {
	return node.array.slice();
}
/** ------------------------------------------------------------------ */
/** Transient vector — mutable accumulator, converted to persistent.   */
/** ------------------------------------------------------------------ */
/**
* TransientVector provides O(1) amortized appends during bulk construction.
* Obtained via `vec.asTransient()`; converted back via `.persistent()`.
*
* Do not use after calling `.persistent()`.
*/
var TransientVector = class {
	_size;
	_shift;
	_root;
	_tail;
	constructor(size, shift, root, tail) {
		this._size = size;
		this._shift = shift;
		this._root = root;
		this._tail = tail;
	}
	get size() {
		return this._size;
	}
	tailOffset() {
		if (this._size < WIDTH) return 0;
		return this._size - 1 >>> BITS$1 << BITS$1;
	}
	append(val) {
		if (this._size - this.tailOffset() < WIDTH) {
			this._tail.push(val);
			this._size++;
			return;
		}
		const tailNode = makeNode(this._tail);
		this._tail = [val];
		if (this._size >>> BITS$1 > 1 << this._shift) {
			this._root = [makeNode(this._root), newPath(this._shift, tailNode)];
			this._shift += BITS$1;
		} else this._root = pushTailMut(this._root, this._shift, this._size - 1, tailNode);
		this._size++;
	}
	persistent() {
		return new PersistentVector(this._size, this._shift, makeNode(this._root), this._tail.slice());
	}
};
function pushTailMut(root, shift, count, tailNode) {
	const newRoot = root.slice();
	const subidx = count - 1 >>> shift & MASK$1;
	if (shift === BITS$1) newRoot[subidx] = tailNode;
	else {
		const child = newRoot[subidx];
		newRoot[subidx] = child !== void 0 ? makeNode(pushTailMut(child.array.slice(), shift - BITS$1, count, tailNode)) : newPath(shift - BITS$1, tailNode);
	}
	return newRoot;
}
/** ------------------------------------------------------------------ */
/** PersistentVector                                                     */
/** ------------------------------------------------------------------ */
var PersistentVector = class PersistentVector {
	static EMPTY = new PersistentVector(0, BITS$1, makeNode([]), []);
	size;
	/** Alias for `size` — lets PersistentVector work where `.length` is expected. */
	get length() {
		return this.size;
	}
	_shift;
	_root;
	_tail;
	constructor(size, shift, root, tail) {
		this.size = size;
		this._shift = shift;
		this._root = root;
		this._tail = tail;
	}
	static empty() {
		return PersistentVector.EMPTY;
	}
	/** Build from a plain JS array. O(N). */
	static from(arr) {
		const t = new TransientVector(0, BITS$1, [], []);
		for (const item of arr) t.append(item);
		return t.persistent();
	}
	tailOffset() {
		if (this.size < WIDTH) return 0;
		return this.size - 1 >>> BITS$1 << BITS$1;
	}
	nodeFor(i) {
		if (i >= this.tailOffset()) return this._tail;
		let node = this._root;
		for (let level = this._shift; level > 0; level -= BITS$1) node = node.array[i >>> level & MASK$1];
		return node.array;
	}
	/** Returns the element at index `i`, or `undefined` if out of range. */
	get(i) {
		if (i < 0 || i >= this.size) return void 0;
		return this.nodeFor(i)[i & MASK$1];
	}
	/** Returns a new vector with `val` at index `i`. Returns `this` if `i` is out of range. */
	set(i, val) {
		if (i < 0 || i >= this.size) return this;
		if (i >= this.tailOffset()) {
			const newTail = this._tail.slice();
			newTail[i & MASK$1] = val;
			return new PersistentVector(this.size, this._shift, this._root, newTail);
		}
		return new PersistentVector(this.size, this._shift, setNode(this._root, this._shift, i, val), this._tail);
	}
	/** Returns a new vector with `val` appended at the end. */
	append(val) {
		if (this.size - this.tailOffset() < WIDTH) return new PersistentVector(this.size + 1, this._shift, this._root, [...this._tail, val]);
		const tailNode = makeNode(this._tail);
		let newShift = this._shift;
		let newRoot;
		if (this.size >>> BITS$1 > 1 << this._shift) {
			newRoot = makeNode([this._root, newPath(this._shift, tailNode)]);
			newShift += BITS$1;
		} else newRoot = pushTailPersistent(this._root, this._shift, this.size - 1, tailNode);
		return new PersistentVector(this.size + 1, newShift, newRoot, [val]);
	}
	/** Returns a new vector with `val` prepended at index 0. O(N). */
	prepend(val) {
		const t = new TransientVector(0, BITS$1, [], []);
		t.append(val);
		for (const item of this) t.append(item);
		return t.persistent();
	}
	/** Returns a new transient for bulk mutation. */
	asTransient() {
		return new TransientVector(this.size, this._shift, this._root.array.slice(), this._tail.slice());
	}
	/** Iterate over elements in order. */
	[Symbol.iterator]() {
		return iterVector(this);
	}
	/** Convert to a plain JS array. O(N). */
	toArray() {
		return [...this];
	}
	/** Returns true if this vector equals another (structural equality of elements via `eq`). */
	equals(other, eq = (a, b) => a === b) {
		if (this === other) return true;
		if (this.size !== other.size) return false;
		let i = 0;
		for (const item of this) {
			if (!eq(item, other.get(i))) return false;
			i++;
		}
		return true;
	}
};
/** Path-copy update of a node. */
function setNode(node, level, i, val) {
	const arr = copyArray(node);
	if (level === 0) arr[i & MASK$1] = val;
	else {
		const subidx = i >>> level & MASK$1;
		arr[subidx] = setNode(node.array[subidx], level - BITS$1, i, val);
	}
	return makeNode(arr);
}
/** Persistent push-tail into trie. */
function pushTailPersistent(node, level, count, tailNode) {
	const arr = copyArray(node);
	const subidx = count - 1 >>> level & MASK$1;
	if (level === BITS$1) arr[subidx] = tailNode;
	else {
		const child = node.array[subidx];
		arr[subidx] = child !== void 0 ? pushTailPersistent(child, level - BITS$1, count, tailNode) : newPath(level - BITS$1, tailNode);
	}
	return makeNode(arr);
}
/** Build a path of single-child nodes down to `node`. */
function newPath(level, node) {
	if (level === 0) return node;
	return makeNode([newPath(level - BITS$1, node)]);
}
/** Iterates a PersistentVector chunk by chunk using the tail optimisation. */
function* iterVector(vec) {
	const size = vec.size;
	for (let i = 0; i < size; i++) yield vec.get(i);
}
//#endregion
//#region src/persistent/PersistentMap.ts
/**
* PersistentMap — an immutable, structurally-shared hash map based on a
* Hash Array Mapped Trie (HAMT). Keys are always strings (Dvala objects
* only use string keys).
*
* Performance characteristics:
*   get(k)    O(log₃₂ N) ≈ O(1) in practice
*   assoc     O(log₃₂ N) — path-copies the spine, shares unchanged nodes
*   dissoc    O(log₃₂ N)
*   has(k)    O(log₃₂ N)
*   size      O(1)
*   iterate   O(N)
*
* HAMT nodes:
*   BitmapNode  — sparse 32-slot virtual node, compressed via popcount bitmap
*   LeafNode    — single key-value pair at a resolved leaf position
*   CollisionNode — multiple pairs sharing the same 32-bit hash
*/
const BITS = 5;
const MASK = (1 << BITS) - 1;
/** djb2-style 32-bit hash for strings. */
function hashCode(s) {
	let h = 0;
	for (let i = 0; i < s.length; i++) h = Math.imul(31, h) + s.charCodeAt(i) | 0;
	return h >>> 0;
}
/** Number of set bits in a 32-bit integer (Hamming weight / popcount). */
function popcount(n) {
	n -= n >>> 1 & 1431655765;
	n = (n & 858993459) + (n >>> 2 & 858993459);
	n = n + (n >>> 4) & 252645135;
	return Math.imul(n, 16843009) >>> 24;
}
/** The bit in the 32-bit virtual slot for `hash` at depth `shift`. */
function bit(hash, shift) {
	return 1 << (hash >>> shift & MASK);
}
/** Index into the compressed children array given `bitmap` and `bit`. */
function bitmapIndex(bitmap, b) {
	return popcount(bitmap & b - 1);
}
function hamtGet(node, key, hash, shift) {
	if (node === null) return void 0;
	if (node.type === "leaf") return node.key === key ? node.value : void 0;
	if (node.type === "collision") {
		if (node.hash !== hash) return void 0;
		const entry = node.entries.find(([k]) => k === key);
		return entry ? entry[1] : void 0;
	}
	const b = bit(hash, shift);
	if (!(node.bitmap & b)) return void 0;
	return hamtGet(node.children[bitmapIndex(node.bitmap, b)], key, hash, shift + BITS);
}
function hamtHas(node, key, hash, shift) {
	return hamtGet(node, key, hash, shift) !== void 0;
}
function hamtInsert(node, key, hash, shift, value, sizeChange) {
	if (node === null) {
		sizeChange.delta = 1;
		return {
			type: "leaf",
			hash,
			key,
			value
		};
	}
	if (node.type === "leaf") {
		if (node.key === key) return {
			...node,
			value
		};
		sizeChange.delta = 1;
		if (node.hash === hash) return {
			type: "collision",
			hash,
			entries: [[node.key, node.value], [key, value]]
		};
		return mergeTwoLeaves(node, {
			type: "leaf",
			hash,
			key,
			value
		}, shift);
	}
	if (node.type === "collision") {
		if (node.hash !== hash) {
			sizeChange.delta = 1;
			return expandCollision(node, {
				type: "leaf",
				hash,
				key,
				value
			}, shift);
		}
		const idx = node.entries.findIndex(([k]) => k === key);
		if (idx >= 0) {
			const newEntries = node.entries.slice();
			newEntries[idx] = [key, value];
			return {
				...node,
				entries: newEntries
			};
		}
		sizeChange.delta = 1;
		return {
			...node,
			entries: [...node.entries, [key, value]]
		};
	}
	const b = bit(hash, shift);
	const idx = bitmapIndex(node.bitmap, b);
	if (node.bitmap & b) {
		const newChild = hamtInsert(node.children[idx], key, hash, shift + BITS, value, sizeChange);
		const newChildren = node.children.slice();
		newChildren[idx] = newChild;
		return {
			type: "bitmap",
			bitmap: node.bitmap,
			children: newChildren
		};
	} else {
		sizeChange.delta = 1;
		const newLeaf = {
			type: "leaf",
			hash,
			key,
			value
		};
		const newChildren = node.children.slice();
		newChildren.splice(idx, 0, newLeaf);
		return {
			type: "bitmap",
			bitmap: node.bitmap | b,
			children: newChildren
		};
	}
}
function hamtRemove(node, key, hash, shift, sizeChange) {
	if (node === null) return null;
	if (node.type === "leaf") {
		if (node.key !== key) return node;
		sizeChange.delta = -1;
		return null;
	}
	if (node.type === "collision") {
		if (node.hash !== hash) return node;
		const newEntries = node.entries.filter(([k]) => k !== key);
		if (newEntries.length === node.entries.length) return node;
		sizeChange.delta = -1;
		if (newEntries.length === 1) return {
			type: "leaf",
			hash,
			key: newEntries[0][0],
			value: newEntries[0][1]
		};
		return {
			...node,
			entries: newEntries
		};
	}
	const b = bit(hash, shift);
	if (!(node.bitmap & b)) return node;
	const idx = bitmapIndex(node.bitmap, b);
	const child = node.children[idx];
	const newChild = hamtRemove(child, key, hash, shift + BITS, sizeChange);
	if (newChild === child) return node;
	if (newChild === null) {
		if (node.children.length === 1) return null;
		const newChildren = node.children.filter((_, i) => i !== idx);
		const newBitmap = node.bitmap ^ b;
		if (newChildren.length === 1 && newChildren[0].type === "leaf") return newChildren[0];
		return {
			type: "bitmap",
			bitmap: newBitmap,
			children: newChildren
		};
	}
	const newChildren = node.children.slice();
	newChildren[idx] = newChild;
	return {
		type: "bitmap",
		bitmap: node.bitmap,
		children: newChildren
	};
}
/** Merge two leaves with different hashes into a new bitmap sub-trie. */
function mergeTwoLeaves(a, b, shift) {
	const ba = bit(a.hash, shift);
	const bb = bit(b.hash, shift);
	if (ba === bb) return {
		type: "bitmap",
		bitmap: ba,
		children: [mergeTwoLeaves(a, b, shift + BITS)]
	};
	const [first, second] = ba >>> 0 < bb >>> 0 ? [a, b] : [b, a];
	return {
		type: "bitmap",
		bitmap: ba | bb,
		children: [first, second]
	};
}
/** Expand a collision node (existing at `shift`) to accommodate a new leaf at a different hash. */
function expandCollision(collision, leaf, shift) {
	const bc = bit(collision.hash, shift);
	const bl = bit(leaf.hash, shift);
	if (bc === bl) return {
		type: "bitmap",
		bitmap: bc,
		children: [expandCollision(collision, leaf, shift + BITS)]
	};
	const [first, second] = bc >>> 0 < bl >>> 0 ? [collision, leaf] : [leaf, collision];
	return {
		type: "bitmap",
		bitmap: bc | bl,
		children: [first, second]
	};
}
function* iterHamt(node) {
	if (node === null) return;
	if (node.type === "leaf") {
		yield [node.key, node.value];
		return;
	}
	if (node.type === "collision") {
		yield* node.entries;
		return;
	}
	for (const child of node.children) yield* iterHamt(child);
}
/**
* TransientMap provides O(1) amortized operations during bulk construction.
* Obtained via `map.asTransient()`; converted back via `.persistent()`.
*
* Do not use after calling `.persistent()`.
*/
var TransientMap = class {
	_entries;
	constructor(source) {
		this._entries = new Map(source ? [...source] : []);
	}
	set(key, value) {
		this._entries.set(key, value);
	}
	persistent() {
		return PersistentMap.from(this._entries);
	}
};
var PersistentMap = class PersistentMap {
	static EMPTY = new PersistentMap(null, 0);
	_root;
	size;
	constructor(root, size) {
		this._root = root;
		this.size = size;
	}
	static empty() {
		return PersistentMap.EMPTY;
	}
	/** Build from an iterable of [key, value] pairs. O(N). */
	static from(entries) {
		let map = PersistentMap.empty();
		for (const [k, v] of entries) map = map.assoc(k, v);
		return map;
	}
	/** Build from a plain JS object. O(N). */
	static fromRecord(record) {
		return PersistentMap.from(Object.entries(record));
	}
	/** Returns the value for `key`, or `undefined` if absent. */
	get(key) {
		return hamtGet(this._root, key, hashCode(key), 0);
	}
	/** Returns true if `key` is present. */
	has(key) {
		return hamtHas(this._root, key, hashCode(key), 0);
	}
	/** Returns a new map with `key` → `value` inserted or updated. */
	assoc(key, value) {
		const sizeChange = { delta: 0 };
		return new PersistentMap(hamtInsert(this._root, key, hashCode(key), 0, value, sizeChange), this.size + sizeChange.delta);
	}
	/** Returns a new map with `key` removed. Returns `this` if not found. */
	dissoc(key) {
		const sizeChange = { delta: 0 };
		const newRoot = hamtRemove(this._root, key, hashCode(key), 0, sizeChange);
		if (sizeChange.delta === 0) return this;
		return new PersistentMap(newRoot, this.size + sizeChange.delta);
	}
	/** Returns an array of all keys. Order is hash-determined (not insertion order). */
	keys() {
		const result = [];
		for (const [k] of this) result.push(k);
		return result;
	}
	/** Returns an array of all values. */
	values() {
		const result = [];
		for (const [, v] of this) result.push(v);
		return result;
	}
	/** Returns an array of [key, value] pairs. */
	entries() {
		const result = [];
		for (const e of this) result.push([e[0], e[1]]);
		return result;
	}
	/** Iterate over [key, value] pairs. */
	[Symbol.iterator]() {
		return iterHamt(this._root);
	}
	/** Convert to a plain JS object (shallow). O(N). */
	toRecord() {
		const result = {};
		for (const [k, v] of this) result[k] = v;
		return result;
	}
	/** Returns a transient for bulk construction. */
	asTransient() {
		return new TransientMap(this);
	}
};
//#endregion
//#region src/persistent/PersistentList.ts
/** Prepend `value` to `list`. */
function cons(value, list) {
	return {
		head: value,
		tail: list
	};
}
/** Convert a PersistentList to a plain JS array. O(N). */
function listToArray(list) {
	const result = [];
	let node = list;
	while (node !== null) {
		result.push(node.head);
		node = node.tail;
	}
	return result;
}
/** Build a PersistentList from a plain JS array. O(N). Head of list = first element. */
function listFromArray(arr) {
	let list = null;
	for (let i = arr.length - 1; i >= 0; i--) list = cons(arr[i], list);
	return list;
}
/** Return the first `n` elements as a new list. O(N). */
function listTake(list, n) {
	const buf = [];
	let node = list;
	while (node !== null && buf.length < n) {
		buf.push(node.head);
		node = node.tail;
	}
	return listFromArray(buf);
}
/** Skip the first `n` elements and return the rest. O(N). */
function listDrop(list, n) {
	let node = list;
	for (let i = 0; i < n && node !== null; i++) node = node.tail;
	return node;
}
/**
* Prepend all elements of `arr` to `list`.
* First element of `arr` becomes the new head. O(N).
* Used for stack reconstruction: e.g. [...innerFrames, handler, ...outerK].
*/
function listPrependAll(arr, list) {
	let result = list;
	for (let i = arr.length - 1; i >= 0; i--) result = cons(arr[i], result);
	return result;
}
/** Return the number of elements in the list. O(N). */
function listSize(list) {
	let n = 0;
	let node = list;
	while (node !== null) {
		n++;
		node = node.tail;
	}
	return n;
}
//#endregion
//#region src/persistent/index.ts
/** Returns true if `value` is a PersistentVector (Dvala array). */
function isPersistentVector(value) {
	return value instanceof PersistentVector;
}
/** Returns true if `value` is a PersistentMap (Dvala object). */
function isPersistentMap(value) {
	return value instanceof PersistentMap;
}
//#endregion
//#region src/reservedNames.ts
const nonNumberReservedSymbolRecord = {
	true: true,
	false: false,
	null: null,
	do: null,
	else: null,
	case: null,
	in: null,
	when: null,
	while: null,
	as: null,
	then: null,
	end: null,
	with: null,
	quote: null,
	_: null
};
const phi = (1 + Math.sqrt(5)) / 2;
const numberReservedSymbolRecord = {
	E: Math.E,
	PI: Math.PI,
	PHI: phi,
	MAX_SAFE_INTEGER: Number.MAX_SAFE_INTEGER,
	MIN_SAFE_INTEGER: Number.MIN_SAFE_INTEGER,
	MAX_VALUE: Number.MAX_VALUE,
	MIN_VALUE: Number.MIN_VALUE
};
const reservedSymbolRecord = {
	...nonNumberReservedSymbolRecord,
	...numberReservedSymbolRecord
};
function isReservedSymbol(symbol) {
	return symbol in reservedSymbolRecord;
}
function isNumberReservedSymbol(symbol) {
	return symbol in numberReservedSymbolRecord;
}
//#endregion
//#region src/specialExpressionTypes.ts
const specialExpressionTypes = {
	"??": "??",
	"&&": "&&",
	"||": "||",
	array: "array",
	function: "function",
	for: "for",
	let: "let",
	loop: "loop",
	object: "object",
	recur: "recur",
	match: "match",
	import: "import",
	effect: "effect",
	perform: "perform",
	parallel: "parallel",
	race: "race",
	settled: "settled"
};
//#endregion
//#region src/ast.ts
const bindingTargetTypes = {
	symbol: "symbol",
	rest: "rest",
	object: "object",
	array: "array",
	literal: "literal",
	wildcard: "wildcard"
};
function resolveSourceCodeInfo(nodeId, sourceMap) {
	if (!sourceMap) return void 0;
	const pos = sourceMap.positions.get(nodeId);
	if (!pos) return void 0;
	const source = sourceMap.sources[pos.source];
	if (!source) return void 0;
	const line = pos.start[0];
	const lines = source.content.split("\n");
	return {
		position: {
			line: line + 1,
			column: pos.start[1] + 1
		},
		code: lines[line] ?? "",
		filePath: source.path === "<anonymous>" ? void 0 : source.path
	};
}
//#endregion
//#region src/debug/debugTools.ts
function isDvalaFunction$1(func) {
	if (func === null || typeof func !== "object") return false;
	return "^^fn^^" in func && "functionType" in func && isFunctionType(func.functionType);
}
function isNode(value) {
	if (!Array.isArray(value) || value.length < 2) return false;
	return isNodeType(value[0]);
}
function valueToString(value) {
	if (isDvalaFunction$1(value)) return `<${value.functionType === "Macro" ? "macro" : "function"} ${value.name || "λ"}>`;
	if (isNode(value)) return `${value[0]}-node`;
	if (value !== null && typeof value === "object" && "^^at^^" in value) return `:${value.name}`;
	if (value === null) return "null";
	if (typeof value === "object" && value instanceof RegExp) return `${value}`;
	if (typeof value === "object" && value instanceof Error) return value.toString();
	return JSON.stringify(value);
}
//#endregion
//#region src/debug/getCodeMarker.ts
function getCodeMarker(sourceCodeInfo) {
	if (!sourceCodeInfo.position || !sourceCodeInfo.code) return "";
	const leftPadding = sourceCodeInfo.position.column - 1;
	const rightPadding = sourceCodeInfo.code.length - leftPadding - 1;
	return `${" ".repeat(Math.max(leftPadding, 0))}^${" ".repeat(Math.max(rightPadding, 0))}`;
}
//#endregion
//#region src/debug/getSourceCodeInfo.ts
function getSourceCodeInfo(anyValue, sourceCodeInfo) {
	return anyValue?.sourceCodeInfo ?? sourceCodeInfo;
}
//#endregion
//#region src/errors.ts
function getDvalaErrorMessage(message, sourceCodeInfo) {
	if (!sourceCodeInfo) return message;
	const location = `${sourceCodeInfo.position.line}:${sourceCodeInfo.position.column}`;
	return `${message}${sourceCodeInfo.filePath ? `\n${sourceCodeInfo.filePath}:${location}` : `\nLocation ${location}`}${`\n${sourceCodeInfo.code}`}${`\n${getCodeMarker(sourceCodeInfo)}`}`;
}
var RecurSignal = class RecurSignal extends Error {
	params;
	constructor(params) {
		super(`recur, params: ${params}`);
		Object.setPrototypeOf(this, RecurSignal.prototype);
		this.name = "RecurSignal";
		this.params = params;
	}
};
var DvalaError = class DvalaError extends Error {
	sourceCodeInfo;
	shortMessage;
	/** Machine-readable error category for the structured @dvala.error payload. */
	errorType = "RuntimeError";
	/** Call stack entries attached when the error propagates out of the evaluator. */
	callStack;
	constructor(err, sourceCodeInfo) {
		const message = err instanceof Error ? err.message : `${err}`;
		super(getDvalaErrorMessage(message, sourceCodeInfo));
		this.shortMessage = message;
		this.sourceCodeInfo = sourceCodeInfo;
		Object.setPrototypeOf(this, DvalaError.prototype);
		this.name = "DvalaError";
	}
	/**
	* Attach a call stack to this error and update the message.
	* Called when the error is about to propagate out of the evaluator
	* (not when caught by an algebraic handler).
	*/
	attachCallStack(entries) {
		if (entries.length === 0) return;
		this.callStack = entries;
		const stackStr = entries.map((entry) => {
			const location = entry.sourceCodeInfo ? `${entry.sourceCodeInfo.filePath ?? ""}:${entry.sourceCodeInfo.position.line}:${entry.sourceCodeInfo.position.column}` : "<unknown>";
			return `  at ${entry.name}  ${location}`;
		}).join("\n");
		this.message = `${this.message}\n${stackStr}`;
	}
	getCodeMarker() {
		return this.sourceCodeInfo && getCodeMarker(this.sourceCodeInfo);
	}
	toJSON() {
		return {
			name: this.name,
			message: this.message,
			shortMessage: this.shortMessage,
			line: this.sourceCodeInfo?.position.line,
			column: this.sourceCodeInfo?.position.column,
			code: this.sourceCodeInfo?.code,
			filePath: this.sourceCodeInfo?.filePath
		};
	}
};
var TokenizerError = class TokenizerError extends DvalaError {
	errorType = "TokenizerError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, TokenizerError.prototype);
		this.name = "TokenizerError";
	}
};
var ParseError = class ParseError extends DvalaError {
	errorType = "ParseError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, ParseError.prototype);
		this.name = "ParseError";
	}
};
var RuntimeError = class RuntimeError extends DvalaError {
	errorType = "RuntimeError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, RuntimeError.prototype);
		this.name = "RuntimeError";
	}
};
var TypeError = class TypeError extends RuntimeError {
	errorType = "TypeError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, TypeError.prototype);
		this.name = "TypeError";
	}
};
var ReferenceError = class ReferenceError extends RuntimeError {
	errorType = "ReferenceError";
	symbol;
	constructor(symbolName, sourceCodeInfo) {
		super(`Undefined symbol '${symbolName}'.`, sourceCodeInfo);
		this.symbol = symbolName;
		Object.setPrototypeOf(this, ReferenceError.prototype);
		this.name = "ReferenceError";
	}
};
var AssertionError = class AssertionError extends RuntimeError {
	errorType = "AssertionError";
	constructor(message, sourceCodeInfo) {
		super(message instanceof Error ? message.message : message, sourceCodeInfo);
		Object.setPrototypeOf(this, AssertionError.prototype);
		this.name = "AssertionError";
	}
};
var ArithmeticError = class ArithmeticError extends RuntimeError {
	errorType = "ArithmeticError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, ArithmeticError.prototype);
		this.name = "ArithmeticError";
	}
};
var MacroError = class MacroError extends RuntimeError {
	errorType = "MacroError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, MacroError.prototype);
		this.name = "MacroError";
	}
};
var UserError = class UserError extends RuntimeError {
	errorType = "UserError";
	userMessage;
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		this.userMessage = message;
		Object.setPrototypeOf(this, UserError.prototype);
		this.name = "UserError";
	}
};
var MatchError = class MatchError extends RuntimeError {
	errorType = "MatchError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, MatchError.prototype);
		this.name = "MatchError";
	}
};
var KeyError = class KeyError extends RuntimeError {
	errorType = "KeyError";
	constructor(message, sourceCodeInfo) {
		super(message, sourceCodeInfo);
		Object.setPrototypeOf(this, KeyError.prototype);
		this.name = "KeyError";
	}
};
function isDvalaError(error) {
	return error instanceof DvalaError;
}
//#endregion
//#region src/getAssertionError.ts
function getAssertionError(typeName, value, sourceCodeInfo) {
	return new TypeError(`Expected ${typeName}, got ${valueToString(value)}.`, getSourceCodeInfo(value, sourceCodeInfo));
}
//#endregion
//#region src/typeGuards/number.ts
function getRangeString(options) {
	if ((typeof options.gt === "number" || typeof options.gte === "number") && (typeof options.lt === "number" || typeof options.lte === "number")) return `${typeof options.gt === "number" ? `${options.gt} < n ` : `${options.gte} <= n `}${typeof options.lt === "number" ? `< ${options.lt}` : `<= ${options.lte}`}`;
	else if (typeof options.gt === "number" || typeof options.gte === "number") return `${typeof options.gt === "number" ? `n > ${options.gt}` : `n >= ${options.gte}`}`;
	else if (typeof options.lt === "number" || typeof options.lte === "number") return `${typeof options.lt === "number" ? `n < ${options.lt}` : `n <= ${options.lte}`}`;
	else return "";
}
function getSignString(options) {
	return options.positive ? "positive" : options.negative ? "negative" : options.nonNegative ? "non negative" : options.nonPositive ? "non positive" : options.nonZero ? "non zero" : "";
}
function getNumberTypeName(options) {
	if (options.zero) return "zero";
	const sign = getSignString(options);
	const numberType = options.integer ? "integer" : "number";
	return [
		sign,
		options.finite ? "finite" : "",
		numberType,
		getRangeString(options)
	].filter((x) => !!x).join(" ");
}
function isNumber(value, options = {}) {
	if (typeof value !== "number") return false;
	if (Number.isNaN(value)) return false;
	if (options.integer && !Number.isInteger(value)) return false;
	if (options.finite && !Number.isFinite(value)) return false;
	if (options.zero && value !== 0) return false;
	if (options.nonZero && value === 0) return false;
	if (options.positive && value <= 0) return false;
	if (options.negative && value >= 0) return false;
	if (options.nonPositive && value > 0) return false;
	if (options.nonNegative && value < 0) return false;
	if (typeof options.gt === "number" && value <= options.gt) return false;
	if (typeof options.gte === "number" && value < options.gte) return false;
	if (typeof options.lt === "number" && value >= options.lt) return false;
	if (typeof options.lte === "number" && value > options.lte) return false;
	return true;
}
function assertNumber(value, sourceCodeInfo, options = {}) {
	if (!isNumber(value, options)) throw new TypeError(`Expected ${getNumberTypeName(options)}, got ${valueToString(value)}.`, getSourceCodeInfo(value, sourceCodeInfo));
}
function asNumber(value, sourceCodeInfo, options = {}) {
	assertNumber(value, sourceCodeInfo, options);
	return value;
}
//#endregion
//#region src/typeGuards/annotatedCollections.ts
const annotatedArrays = /* @__PURE__ */ new WeakSet();
const annotatedVectors = /* @__PURE__ */ new WeakSet();
const annotadedNonVectors = /* @__PURE__ */ new WeakSet();
const annotadedMatrices = /* @__PURE__ */ new WeakSet();
const annotatedNonMatrices = /* @__PURE__ */ new WeakSet();
const annotatedGrids = /* @__PURE__ */ new WeakSet();
const annotatedNonGrids = /* @__PURE__ */ new WeakSet();
/**
* If value is a PersistentVector, convert it (deeply) to a plain JS array.
* Otherwise, return the value unchanged. This is used at module boundaries
* where downstream code expects plain arrays with bracket indexing.
*/
function toPlainArray(value) {
	if (isPersistentVector(value)) {
		const arr = [];
		for (const item of value) arr.push(toPlainArray(item));
		return arr;
	}
	if (Array.isArray(value)) return value.map(toPlainArray);
	return value;
}
function isVector(vector) {
	const plain = toPlainArray(vector);
	if (!Array.isArray(plain)) return false;
	if (annotatedVectors.has(plain)) return true;
	if (annotadedNonVectors.has(plain)) return false;
	if (plain.every((elem) => isNumber(elem))) {
		annotatedArrays.add(plain);
		annotatedVectors.add(plain);
		return true;
	}
	annotadedNonVectors.add(plain);
	return false;
}
/**
* Assert that `vector` is a numeric vector. If it's a PersistentVector,
* converts to a plain `number[]` and returns it. Callers should use the
* return value: `vector = assertVector(vector, sci)`
*/
function assertVector(vector, sourceCodeInfo) {
	const plain = toPlainArray(vector);
	if (!isVector(plain)) throw new TypeError(`Expected a vector, but got ${vector}`, sourceCodeInfo);
	return plain;
}
function is2dVector(vector) {
	const plain = toPlainArray(vector);
	if (!isVector(plain)) return false;
	return plain.length === 2;
}
function assert2dVector(vector, sourceCodeInfo) {
	const plain = toPlainArray(vector);
	if (!is2dVector(plain)) throw new TypeError(`Expected a 2d vector, but got ${vector}`, sourceCodeInfo);
	return plain;
}
function is3dVector(vector) {
	const plain = toPlainArray(vector);
	if (!isVector(plain)) return false;
	return plain.length === 3;
}
function assert3dVector(vector, sourceCodeInfo) {
	const plain = toPlainArray(vector);
	if (!is3dVector(plain)) throw new TypeError(`Expected a 3d vector, but got ${vector}`, sourceCodeInfo);
	return plain;
}
function assertNonEmptyVector(vector, sourceCodeInfo) {
	const plain = assertVector(vector, sourceCodeInfo);
	if (plain.length === 0) throw new TypeError(`Expected a non empty vector, but got ${vector}`, sourceCodeInfo);
	return plain;
}
function isGrid(grid, typePred) {
	const plain = toPlainArray(grid);
	if (!Array.isArray(plain)) return false;
	if (annotatedGrids.has(plain)) return true;
	if (annotatedNonGrids.has(plain)) return false;
	if (plain.length === 0) {
		annotatedNonGrids.add(plain);
		return false;
	}
	if (!Array.isArray(plain[0])) {
		annotatedNonGrids.add(plain);
		return false;
	}
	const nbrOfCols = plain[0].length;
	if (nbrOfCols === 0) {
		annotatedNonGrids.add(plain);
		return false;
	}
	for (const row of plain) {
		if (!Array.isArray(row)) {
			annotatedNonGrids.add(plain);
			return false;
		}
		if (row.length !== nbrOfCols) {
			annotatedNonGrids.add(plain);
			return false;
		}
		if (typePred && row.some((cell) => !typePred(cell))) return false;
	}
	annotatedArrays.add(plain);
	annotatedGrids.add(plain);
	return true;
}
function assertGrid(grid, sourceCodeInfo) {
	const plain = toPlainArray(grid);
	if (!isGrid(plain)) throw new TypeError(`Expected a grid, but got ${grid}`, sourceCodeInfo);
	return plain;
}
function isMatrix(matrix) {
	const plain = toPlainArray(matrix);
	if (!isGrid(plain, isNumber)) {
		if (Array.isArray(plain)) annotatedNonMatrices.add(plain);
		return false;
	}
	annotadedMatrices.add(plain);
	return true;
}
function assertMatrix(matrix, sourceCodeInfo) {
	const plain = toPlainArray(matrix);
	if (!isMatrix(plain)) throw new TypeError(`Expected a matrix, but got ${matrix}`, sourceCodeInfo);
	return plain;
}
function assertSquareMatrix(matrix, sourceCodeInfo) {
	const plain = toPlainArray(matrix);
	if (!isMatrix(plain)) throw new TypeError(`Expected a matrix, but got ${matrix}`, sourceCodeInfo);
	if (plain.length !== plain[0].length) throw new TypeError(`Expected square matrix, but got ${plain.length} and ${plain[0].length}`, sourceCodeInfo);
	return plain;
}
function isSquareMatrix(matrix) {
	const plain = toPlainArray(matrix);
	if (!isMatrix(plain)) return false;
	if (plain.length !== plain[0].length) return false;
	return true;
}
//#endregion
//#region src/typeGuards/array.ts
/** Assert that `value` is a Dvala array (PersistentVector). */
function asArray(value, sourceCodeInfo) {
	assertArray(value, sourceCodeInfo);
	return value;
}
function assertArray(value, sourceCodeInfo) {
	if (!isPersistentVector(value)) throw getAssertionError("array", value, sourceCodeInfo);
}
function isStringArray(value) {
	if (!isPersistentVector(value)) return false;
	for (const item of value) if (typeof item !== "string") return false;
	return true;
}
function asStringArray(value, sourceCodeInfo) {
	assertStringArray(value, sourceCodeInfo);
	return value;
}
function assertStringArray(value, sourceCodeInfo) {
	if (!isStringArray(value)) throw getAssertionError("array of strings", value, sourceCodeInfo);
}
function isCharArray(value) {
	if (!isPersistentVector(value)) return false;
	for (const item of value) if (typeof item !== "string" || item.length !== 1) return false;
	return true;
}
function asCharArray(value, sourceCodeInfo) {
	assertCharArray(value, sourceCodeInfo);
	return value;
}
function assertCharArray(value, sourceCodeInfo) {
	if (!isCharArray(value)) throw getAssertionError("array of strings", value, sourceCodeInfo);
}
//#endregion
//#region src/typeGuards/astNode.ts
function isSymbolNode(node) {
	const nodeType = node[0];
	return NodeTypes.Sym === nodeType || NodeTypes.Builtin === nodeType || NodeTypes.Special === nodeType;
}
function asSymbolNode(node, sourceCodeInfo) {
	assertSymbolNode(node, sourceCodeInfo);
	return node;
}
function assertSymbolNode(node, sourceCodeInfo) {
	if (!isSymbolNode(node)) throw getAssertionError("SymbolNode", node, sourceCodeInfo);
}
function isUserDefinedSymbolNode(node) {
	return NodeTypes.Sym === node[0];
}
function asUserDefinedSymbolNode(node, sourceCodeInfo) {
	assertUserDefinedSymbolNode(node, sourceCodeInfo);
	return node;
}
function assertUserDefinedSymbolNode(node, sourceCodeInfo) {
	if (!isUserDefinedSymbolNode(node)) throw getAssertionError("SymNode", node, sourceCodeInfo);
}
function isBuiltinSymbolNode(node) {
	return NodeTypes.Builtin === node[0];
}
function isSpecialSymbolNode(node) {
	return NodeTypes.Special === node[0];
}
function isNormalExpressionNode(node) {
	return node[0] === NodeTypes.Call;
}
function asNormalExpressionNode(node, sourceCodeInfo) {
	assertNormalExpressionNode(node, sourceCodeInfo);
	return node;
}
function assertNormalExpressionNode(node, sourceCodeInfo) {
	if (!isNormalExpressionNode(node)) throw getAssertionError("NormalExpressionNode", node, sourceCodeInfo);
}
function isNormalExpressionNodeWithName(node) {
	if (!isNormalExpressionNode(node)) return false;
	return isSymbolNode(node[1][0]);
}
function asNormalExpressionNodeWithName(node, sourceCodeInfo) {
	assertNormalExpressionNodeWithName(node, sourceCodeInfo);
	return node;
}
function assertNormalExpressionNodeWithName(node, sourceCodeInfo) {
	if (!isNormalExpressionNodeWithName(node)) throw getAssertionError("NormalExpressionNodeWithName", node, sourceCodeInfo);
}
function isExpressionNode(node) {
	return isNormalExpressionNode(node) || node[0] === NodeTypes.SpecialExpression || node[0] === NodeTypes.Num || node[0] === NodeTypes.Str || node[0] === NodeTypes.TmplStr;
}
function asExpressionNode(node, sourceCodeInfo) {
	assertExpressionNode(node, sourceCodeInfo);
	return node;
}
function assertExpressionNode(node, sourceCodeInfo) {
	if (!isExpressionNode(node)) throw getAssertionError("ExpressionNode", node, sourceCodeInfo);
}
function isSpreadNode(node) {
	return node[0] === NodeTypes.Spread;
}
//#endregion
//#region src/typeGuards/string.ts
function isString(value, options = {}) {
	if (typeof value !== "string") return false;
	if (options.nonEmpty && value.length === 0) return false;
	if (options.char && value.length !== 1) return false;
	return true;
}
function assertString(value, sourceCodeInfo, options = {}) {
	if (!isString(value, options)) throw getAssertionError(`${options.nonEmpty ? "non empty string" : options.char ? "character" : "string"}`, value, sourceCodeInfo);
}
function asString(value, sourceCodeInfo, options = {}) {
	assertString(value, sourceCodeInfo, options);
	return value;
}
function isStringOrNumber(value) {
	return typeof value === "string" || typeof value === "number";
}
function asStringOrNumber(value, sourceCodeInfo) {
	assertStringOrNumber(value, sourceCodeInfo);
	return value;
}
function assertStringOrNumber(value, sourceCodeInfo) {
	if (!isStringOrNumber(value)) throw getAssertionError("string or number", value, sourceCodeInfo);
}
//#endregion
//#region src/typeGuards/index.ts
function isNonUndefined(value) {
	return value !== void 0;
}
function asNonUndefined(value, sourceCodeInfo) {
	assertNonUndefined(value, sourceCodeInfo);
	return value;
}
function assertNonUndefined(value, sourceCodeInfo) {
	if (!isNonUndefined(value)) throw new TypeError("Unexpected undefined", getSourceCodeInfo(value, sourceCodeInfo));
}
/**
* Returns true if `value` is a plain JS record (not a Dvala PersistentVector,
* PersistentMap, RegExp, or other special object). Used for internal frame
* data, not for Dvala value checks (use `isObj` for Dvala objects).
*/
function isUnknownRecord(value) {
	return value !== null && typeof value === "object" && !Array.isArray(value) && !isPersistentVector(value) && !isPersistentMap(value);
}
function assertUnknownRecord(value, sourceCodeInfo) {
	if (!isUnknownRecord(value)) throw new TypeError(`Expected UnknownRecord, got ${valueToString(value)}.`, getSourceCodeInfo(value, sourceCodeInfo));
}
function asUnknownRecord(value, sourceCodeInfo) {
	assertUnknownRecord(value, sourceCodeInfo);
	return value;
}
//#endregion
//#region src/typeGuards/dvalaFunction.ts
function isDvalaFunction(value) {
	if (value === null || typeof value !== "object") return false;
	return !!value[FUNCTION_SYMBOL];
}
function asDvalaFunction(value, sourceCodeInfo) {
	assertDvalaFunction(value, sourceCodeInfo);
	return value;
}
function assertDvalaFunction(value, sourceCodeInfo) {
	if (!isDvalaFunction(value)) throw getAssertionError("DvalaFunction", value, sourceCodeInfo);
}
function isUserDefinedFunction(value) {
	return isDvalaFunction(value) && value.functionType === "UserDefined";
}
function asUserDefinedFunction(value, sourceCodeInfo) {
	assertUserDefinedFunction(value, sourceCodeInfo);
	return value;
}
function assertUserDefinedFunction(value, sourceCodeInfo) {
	if (!isUserDefinedFunction(value)) throw getAssertionError("UserDefinedFunction", value, sourceCodeInfo);
}
function isMacroFunction(value) {
	return isDvalaFunction(value) && value.functionType === "Macro";
}
function isBuiltinFunction(value) {
	return isUnknownRecord(value) && value.functionType === "Builtin";
}
function isHandlerFunction(value) {
	return isDvalaFunction(value) && value.functionType === "Handler";
}
//#endregion
//#region src/typeGuards/dvala.ts
function isAny(value) {
	return value !== void 0;
}
function asAny(value, sourceCodeInfo) {
	assertAny(value, sourceCodeInfo);
	return value;
}
function assertAny(value, sourceCodeInfo) {
	if (!isAny(value)) throw getAssertionError("not undefined", value, sourceCodeInfo);
}
function isSeq(value) {
	return isPersistentVector(value) || typeof value === "string";
}
function asSeq(value, sourceCodeInfo) {
	assertSeq(value, sourceCodeInfo);
	return value;
}
function assertSeq(value, sourceCodeInfo) {
	if (!isSeq(value)) throw getAssertionError("string or array", value, sourceCodeInfo);
}
function isArr(value) {
	return isPersistentVector(value);
}
function isObj(value) {
	return isPersistentMap(value);
}
function asObj(value, sourceCodeInfo) {
	assertObj(value, sourceCodeInfo);
	return value;
}
function assertObj(value, sourceCodeInfo) {
	if (!isObj(value)) throw getAssertionError("object", value, sourceCodeInfo);
}
function isColl(value) {
	return isSeq(value) || isObj(value);
}
function asColl(value, sourceCodeInfo) {
	assertColl(value, sourceCodeInfo);
	return value;
}
function assertColl(value, sourceCodeInfo) {
	if (!isColl(value)) throw getAssertionError("string, array or object", value, sourceCodeInfo);
}
function isRegularExpression(regexp) {
	if (regexp === null || typeof regexp !== "object") return false;
	return !!regexp[REGEXP_SYMBOL];
}
function asRegularExpression(value, sourceCodeInfo) {
	assertRegularExpression(value, sourceCodeInfo);
	return value;
}
function assertRegularExpression(value, sourceCodeInfo) {
	if (!isRegularExpression(value)) throw getAssertionError("RegularExpression", value, sourceCodeInfo);
}
function isStringOrRegularExpression(value) {
	return isRegularExpression(value) || typeof value === "string";
}
function asStringOrRegularExpression(value, sourceCodeInfo) {
	assertStringOrRegularExpression(value, sourceCodeInfo);
	return value;
}
function assertStringOrRegularExpression(value, sourceCodeInfo) {
	if (!isStringOrRegularExpression(value)) throw getAssertionError("string or RegularExpression", value, sourceCodeInfo);
}
function isAtom(value) {
	if (value === null || typeof value !== "object") return false;
	return !!value[ATOM_SYMBOL];
}
function isEffect(value) {
	if (value === null || typeof value !== "object") return false;
	return !!value[EFFECT_SYMBOL];
}
function asEffect(value, sourceCodeInfo) {
	assertEffect(value, sourceCodeInfo);
	return value;
}
function assertEffect(value, sourceCodeInfo) {
	if (!isEffect(value)) throw getAssertionError("Effect", value, sourceCodeInfo);
}
function isFunctionLike(value) {
	if (typeof value === "number") return true;
	if (isColl(value)) return true;
	if (isDvalaFunction(value)) return true;
	return false;
}
function asFunctionLike(value, sourceCodeInfo) {
	assertFunctionLike(value, sourceCodeInfo);
	return value;
}
function assertFunctionLike(value, sourceCodeInfo) {
	if (!isFunctionLike(value)) throw getAssertionError("FunctionLike", value, sourceCodeInfo);
}
//#endregion
//#region src/arityValidator.ts
function arityAccepts(arity, nbrOfParams) {
	const { min, max } = arity;
	if (typeof min === "number" && nbrOfParams < min) return false;
	if (typeof max === "number" && nbrOfParams > max) return false;
	return true;
}
function arityAcceptsMin(arity, nbrOfParams) {
	const { min } = arity;
	if (typeof min === "number" && nbrOfParams < min) return false;
	return true;
}
function getCommonArityFromFunctions(params) {
	return params.reduce((acc, param) => {
		if (acc === null) return null;
		const { min: aMin, max: aMax } = typeof param === "number" || isColl(param) ? toFixedArity(1) : param.arity;
		const { min: bMin, max: bMax } = acc;
		const min = typeof aMin === "number" && typeof bMin === "number" ? Math.max(aMin, bMin) : typeof aMin === "number" ? aMin : typeof bMin === "number" ? bMin : void 0;
		const max = typeof aMax === "number" && typeof bMax === "number" ? Math.min(aMax, bMax) : typeof aMax === "number" ? aMax : typeof bMax === "number" ? bMax : void 0;
		if (typeof min === "number" && typeof max === "number" && min > max) return null;
		return {
			min,
			max
		};
	}, {});
}
function getArityFromFunction(param) {
	return typeof param === "number" || isColl(param) ? toFixedArity(1) : param.arity;
}
function assertNumberOfParams(arity, length, sourceCodeInfo) {
	const { min, max } = arity;
	if (typeof min === "number" && length < min) throw new TypeError(`Wrong number of arguments, expected at least ${min}, got ${valueToString(length)}.`, sourceCodeInfo);
	if (typeof max === "number" && length > max) throw new TypeError(`Wrong number of arguments, expected at most ${max}, got ${valueToString(length)}.`, sourceCodeInfo);
}
function canBeOperator(count) {
	if (typeof count.max === "number" && count.max < 2) return false;
	if (typeof count.min === "number" && count.min > 2) return false;
	return true;
}
function toFixedArity(arity) {
	return {
		min: arity,
		max: arity
	};
}
//#endregion
export { ATOM_SYMBOL, ArithmeticError, AssertionError, DvalaError, EFFECT_SYMBOL, FUNCTION_SYMBOL, KeyError, MAX_MACRO_EXPANSION_DEPTH, MacroError, MatchError, NodeTypes, ParseError, PersistentMap, PersistentVector, REGEXP_SYMBOL, RecurSignal, ReferenceError, RuntimeError, TokenizerError, TypeError, UserError, arityAccepts, arityAcceptsMin, asAny, asArray, asCharArray, asColl, asDvalaFunction, asEffect, asExpressionNode, asFunctionLike, asNonUndefined, asNormalExpressionNode, asNormalExpressionNodeWithName, asNumber, asObj, asRegularExpression, asSeq, asString, asStringArray, asStringOrNumber, asStringOrRegularExpression, asSymbolNode, asUnknownRecord, asUserDefinedFunction, asUserDefinedSymbolNode, assert2dVector, assert3dVector, assertAny, assertArray, assertCharArray, assertColl, assertDvalaFunction, assertEffect, assertExpressionNode, assertFunctionLike, assertGrid, assertMatrix, assertNonEmptyVector, assertNonUndefined, assertNormalExpressionNode, assertNormalExpressionNodeWithName, assertNumber, assertNumberOfParams, assertObj, assertRegularExpression, assertSeq, assertSquareMatrix, assertString, assertStringArray, assertStringOrNumber, assertStringOrRegularExpression, assertSymbolNode, assertUnknownRecord, assertUserDefinedFunction, assertVector, bindingTargetTypes, canBeOperator, cons, getArityFromFunction, getAssertionError, getCodeMarker, getCommonArityFromFunctions, getSourceCodeInfo, is2dVector, is3dVector, isAny, isArr, isAtom, isBuiltinFunction, isBuiltinSymbolNode, isCharArray, isColl, isDvalaError, isDvalaFunction, isEffect, isExpressionNode, isFunctionType, isGrid, isHandlerFunction, isMacroFunction, isMatrix, isNodeType, isNormalExpressionNode, isNormalExpressionNodeWithName, isNumber, isNumberReservedSymbol, isObj, isPersistentMap, isPersistentVector, isRegularExpression, isReservedSymbol, isSeq, isSpecialSymbolNode, isSpreadNode, isSquareMatrix, isString, isStringArray, isStringOrNumber, isStringOrRegularExpression, isSymbolNode, isUnknownRecord, isUserDefinedFunction, isUserDefinedSymbolNode, isVector, listDrop, listFromArray, listPrependAll, listSize, listTake, listToArray, numberReservedSymbolRecord, reservedSymbolRecord, resolveSourceCodeInfo, specialExpressionTypes, toFixedArity, valueToString };

//# sourceMappingURL=index.js.map